import React, { useRef } from 'react';
import styled from 'styled-components';
import emojiMonocle from './img/emoji-monocle.png';
import RatingGroup from 'Components/RatingButton/RatingButtonGroup';
import SimpleTextarea from 'Components/Textarea/SimpleTextarea';
import ThemeButton from 'Components/ThemedButton';
import { Modal, ModalHeader } from 'Containers/Modals/styled';
import { EnumEvents, EnumNotification, EnumSize } from 'constants/enums';
import { EMPTY_STRING } from 'constants/constants';
import { useDispatch, useSelector } from 'react-redux';
import { setNotificationAction } from 'Modules/Notifications/actions';
import { getCurrentUser } from 'Modules/AAA/selectors';
import amplitude from 'utils/amplitude';
import { mediaSmDown } from 'theme/helpers/css';
import Title from 'Components/Typography/Title';
import emojiHands from './img/emoji-hands.png';
import { withSpaceTrim } from '../Input/hocs';
import Input from '../Input/Input';
import { Box } from '@igooods/ui-kit';
import { EnumButtonSize } from 'constants/enums';
import { Field, Formik, FieldProps } from 'formik';
import { object, string } from 'yup';
import FeedbackHttp from '../../Modules/Feedback/http';

interface IFeedbackForm {
  successSendFeedback: () => void;
}

const Form = styled.form`
  margin-top: 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const Rating = styled(RatingGroup)`
  justify-content: center;
  margin-bottom: 40px;

  ${mediaSmDown`
    margin-bottom: 16px;
  `}
`;

const FeedbackTextarea = styled(SimpleTextarea)`
  height: 160px;

  ${mediaSmDown`
    height: 80px;
  `}
`;

const FeedbackSubmit = styled(ThemeButton)`
  ${mediaSmDown`
    margin-top: auto;
  `}
`;

const Image = styled.div`
  width: 60px;
  height: 60px;
  background-size: contain;
  background-image: url(${emojiMonocle});
  margin: 0 auto;
`;

export const SUCCESS_MESSAGE = 'Спасибо за отзыв';
export const ERROR_MESSAGE = 'Что-то пошло не так, попробуйте повторить';

export const FEEDBACK_FORM_TEST_ID = 'FEEDBACK_FORM_TEST_ID';
export const FEEDBACK_FORM_EMAIL_INPUT_ID = 'FEEDBACK_FORM_EMAIL_INPUT_ID';
export const FEEDBACK_FORM_COMMENT_TEXTAREA_ID = 'FEEDBACK_FORM_COMMENT_TEXTAREA_ID';
export const FEEDBACK_FORM_SUBMIT_BTN_ID = 'FEEDBACK_FORM_SUBMIT_BTN_ID';

enum FeedbackFormDataFields {
  rating = 'rating',
  comment = 'comment',
  email = 'email',
}

const EmailInput = withSpaceTrim(Input);

const emailErrorText = 'Разрешен e-mail вида name@mail.com';

const DisplayingErrorMessagesSchema = object().shape({
  [FeedbackFormDataFields.email]: string().email(emailErrorText),
});

const INITIAL_RATING = 0;

const FeedbackForm: React.FC<IFeedbackForm> = ({ successSendFeedback }) => {
  const dispatch = useDispatch();
  const { email } = useSelector(getCurrentUser);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  return (
    <Modal data-testid={FEEDBACK_FORM_TEST_ID}>
      <ModalHeader>
        <Image />
        <Title center level="2">
          Что вы думаете о&nbsp;сайте?
        </Title>
      </ModalHeader>
      <Formik
        initialValues={{
          rating: INITIAL_RATING,
          comment: EMPTY_STRING,
          email: email || EMPTY_STRING,
        }}
        enableReinitialize
        onSubmit={async ({ rating: newRating, comment: newComment, email: newEmail }) => {
          try {
            await FeedbackHttp.sendFeedbackSite({
              rating: newRating,
              comment: newComment,
              email: newEmail,
            });

            amplitude.logEvent(EnumEvents.FEEDBACK_SUBMIT_FORM);

            successSendFeedback();
            dispatch(setNotificationAction(SUCCESS_MESSAGE, EnumNotification.success, emojiHands));
          } catch (e) {
            dispatch(setNotificationAction(ERROR_MESSAGE, EnumNotification.danger));
          }
        }}
        validationSchema={DisplayingErrorMessagesSchema}
      >
        {({ handleSubmit, values, isValid, touched, errors, isSubmitting, setFieldValue }) => (
          <Form onSubmit={handleSubmit}>
            <Rating
              rating={values[FeedbackFormDataFields.rating]}
              onClick={r => {
                setFieldValue(FeedbackFormDataFields.rating, r);
                commentRef?.current?.focus();
              }}
              size={EnumSize.lg}
            />
            <Box mb={5} mbXs={2}>
              <Field name={FeedbackFormDataFields.comment}>
                {({ field }: FieldProps) => (
                  <FeedbackTextarea
                    ref={commentRef}
                    {...field}
                    data-testid={FEEDBACK_FORM_COMMENT_TEXTAREA_ID}
                    maxLength={1000}
                    placeholder="Ваши впечатления"
                  />
                )}
              </Field>
            </Box>
            <Box mb={11} mbXs={5}>
              <Field
                name={FeedbackFormDataFields.email}
                maxLength={50}
                as={EmailInput}
                placeholder="E-mail (для обратной связи)"
                error={touched[FeedbackFormDataFields.email] && errors[FeedbackFormDataFields.email]}
                testId={FEEDBACK_FORM_EMAIL_INPUT_ID}
                type="text"
                inputMode="email"
              />
            </Box>
            <FeedbackSubmit
              type="submit"
              testId={FEEDBACK_FORM_SUBMIT_BTN_ID}
              disabled={!isValid || values[FeedbackFormDataFields.rating] === INITIAL_RATING}
              size={EnumButtonSize.large}
              isLoading={isSubmitting}
              fluid
            >
              Отправить
            </FeedbackSubmit>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default FeedbackForm;
